import { useEffect, useMemo, useState } from "react"
import { numberFormat, safeParseInt, dateTimeStampFormat } from "shared/util"
import {
	Box, BoxProps, Stack, Paper,
	Typography, Link, Divider, Alert, Switch	
} from "@mui/material"

import { Loading } from "components/controls/loading"
import { ValueSpan } from "components/controls/valueSpan"

import { DiseaseResultList, useListDpcDisease } from "interface/dpc"
import { useGetManufacturerLogo } from "interface/manufacturer"
import { SearchDetailResult } from "interface/search"
import { SearchsDiseaseResult } from "interface/search/disease"
import { NumericInput } from "components/controls/numbericInput"
import { useGrid } from "components/grid"
import { BlockCaption } from "components/controls/blockCaption"
import { useUserSession } from "features/session/context"
import { useGetICD10DiseasesNode } from "interface/icd10"
import { Section } from "components/controls/section"

type DiseaseRecordProps = {
	dpcList: DiseaseResultList | null
	disease: SearchsDiseaseResult
} & BoxProps
const DiseaseRecord = ({
	dpcList,
	disease,
	...others
}: DiseaseRecordProps) => {
	const dpcDisease = useMemo(
		() => dpcList?.list.find(d => d.dpcCode === disease.dpcCode),
		[dpcList, disease.dpcCode]
	)
	const icd10node = useGetICD10DiseasesNode(disease.icd10code)
	return (
		<Box {...others}>
			<Typography variant="body2">{disease.diseaseName} / {disease.searchWord}</Typography>				
			{(disease.icd10code && disease.icd10code !== "") && (
				<Typography component="div" variant="caption" marginLeft={1}>
					ICD10: {icd10node.data?.nodeName ?? ''} [{disease.icd10code}] 
				</Typography>
			)}
			{(disease.dpcCode && disease.dpcCode !== "") && (
				<Typography component="div" variant="caption" marginLeft={1}>
					DPC: {dpcDisease?.diseaseName ?? ''} [{disease.dpcCode}]
				</Typography>
			)}
		</Box>
	)
}

type InfoPageProps = {
	search: SearchDetailResult
} & ReturnType<typeof useGrid>
export const InfoPage = ({
	search,
	options,
	setOptions
}: InfoPageProps) => {
	const session = useUserSession()
	const dpcListState = useListDpcDisease('all')

	const [manufacturerLogoImage, setManufacturerLogoImage] = useState<string | undefined>(undefined)
  const manufacturer = useMemo(
    () => search.manufacturer ? {
			manufacturerId: search.manufacturer.manufacturerId,
			updateTime: search.manufacturer.updateTime
		} : undefined,
    [search]
  )
  const manufacturerLogoState = useGetManufacturerLogo(manufacturer?.manufacturerId, manufacturer?.updateTime)
  useEffect(
    () => setManufacturerLogoImage(manufacturerLogoState.data ? URL.createObjectURL(manufacturerLogoState.data.blob) : undefined),
    [manufacturerLogoState.data]
  )
	return (
		<Paper variant="outlined" sx={{ boxSizing: 'border-box', height: '100%', width: '100%', position: 'relative', overflow: 'auto' }}>
			<Stack direction="column">
				{dpcListState.loading && <Loading/>}

				<Stack direction="row" spacing={2} justifyContent="center" alignItems="center" padding={2} flexWrap="wrap">
					<Stack direction="row" spacing={1} justifyContent="center" alignItems="center" flexGrow={1}>
						{manufacturerLogoImage && <img alt={search.title} src={manufacturerLogoImage} style={{ borderRadius: 1, width: '100px' }}/> }
						<Typography component="div" variant="h4" color="text.primary" fontWeight="bold" flexGrow={1}>{search.title}</Typography>
					</Stack>
					<ValueSpan prefix="登録" variant="body2" text={dateTimeStampFormat(search.createTime)}/>
					<ValueSpan prefix="更新" variant="body2" text={dateTimeStampFormat(search.updateTime)}/>
				</Stack>

				<Divider/>

				{search.status !== 8 ? (
					<Box padding={1}>
						<Alert severity="info">分析が完了していません</Alert>
					</Box>
				) : (
					<>
						<Stack direction="column" padding={1}>
							<BlockCaption caption="情報ソース"/>

							{search.disease && search.disease.length > 0 && (
								<Section label="疾患名">
									{search.disease.map((disease, i) => (
										<DiseaseRecord key={`disease-row-${i}`} dpcList={dpcListState.data} disease={disease}/>
									))}
								</Section>
							)}

							{search.drug && search.drug.length > 0 && (
								<Section label="薬剤名">
									{search.drug.map((drug, i) => (
										<Box key={`drug-row-${i}`}>
											<Typography variant="body2">{drug.drugName}{drug.isOwned ? ' [自社]' : ''}</Typography>				
										</Box>
									))}
								</Section>
							)}

							{search.senmoni && search.senmoni.length > 0 && (
								<Section label="対象専門医">
									{search.senmoni.map((senmoni, i) => (
										<Box key={`senmoni-row-${i}`}>
											<Typography variant="body2">{senmoni.senmoniName}</Typography>				
										</Box>
									))}
								</Section>
							)}

							{(
								(search.guideLine && search.guideLine.length > 0) ||
								(search.convention && search.convention.length > 0)
							) && (
								<Section label="外部ソース">
									{search.convention?.map((convention, i) => (
										<Box key={`convention-row-${i}`}>
											{convention.officialUrl ? (
												<Link href={convention.officialUrl} target="_blank" variant="body2" underline='hover'>
													{`${convention.numberOfTimes} ${convention.conventionName}`}
												</Link>
											) : (
												<Typography component="div" variant="body2">
													{convention.numberOfTimes} {convention.conventionName}
												</Typography>				
											)}
											{convention.description ? (
												<Typography component="pre" variant="caption" marginLeft={1}>
													{convention.description}
												</Typography>
											) : <></>}
										</Box>
									))}
									{search.guideLine?.map((guideLine, i) => (
										<Box key={`guideLine-row-${i}`}>
											{guideLine.sourceUrl ? (
												<Link href={guideLine.sourceUrl} target="_blank" variant="body2" underline='hover'>
													{guideLine.guideLineName}
												</Link>
											) : (
												<Typography component="div" variant="body2">
													{guideLine.guideLineName}
												</Typography>
											)}
											{guideLine.description ? (
												<Typography component="pre" variant="caption" marginLeft={1}>
													{guideLine.description}
												</Typography>
											) : <></>}
										</Box>
									))}
								</Section>
							)}
						</Stack>

						<Stack direction="column" padding={1}>
							<BlockCaption caption="分析結果"/>

							{((search.sisetuCount ?? 0) > 0) && (
								<Section label="対象施設数">
									<ValueSpan
										text={numberFormat(search.sisetuCount)}
										align="left"
										suffix="施設"
									/>
								</Section>
							)}

							{((search.kojinCount ?? 0) > 0) && (
								<Section label="対象医師数">
									<ValueSpan
										text={numberFormat(search.kojinCount)}
										align="left"
										suffix="人"
									/>
								</Section>
							)}

							{((search.journalCount ?? 0) > 0) && (
								<Section label="対象論文数">
									<ValueSpan
										text={numberFormat(search.journalCount)}
										align="left"
										suffix="件"
									/>
								</Section>
							)}

							{((search.trialCount ?? 0) > 0) && (
								<Section label="対象治験数">
									<ValueSpan
										text={numberFormat(search.trialCount)}
										suffix="件"
										align="left"
									/>
								</Section>
							)}

							{((search.researchCount ?? 0) > 0) && (
								<Section label="対象研究数">
									<ValueSpan
										text={numberFormat(search.researchCount)}
										suffix="件"
										align="left"
									/>
								</Section>
							)}
						</Stack>

						<Stack direction="column" padding={1}>
							<BlockCaption caption="オプション"/>

							<Section label="施設表示件数" labelProps={{ py: "18px" }}>
								<NumericInput
									state={`${options.sisetuLimit}`}
									disabled={session.isCareNet === false}
									setState={(text) => setOptions({ ...options, sisetuLimit: safeParseInt(text) ?? 1000 })}
									style={{ width: '10em' }}
									suffix="位まで"
								/>
							</Section>

							<Section label="医師表示件数" labelProps={{ py: "18px" }}>
								<NumericInput
									state={`${options.kojinLimit}`}
									disabled={session.isCareNet === false}
									setState={(text) => setOptions({ ...options, kojinLimit: safeParseInt(text) ?? 1000 })}
									style={{ width: '10em' }}
									suffix="位まで"
								/>
							</Section>

							<Section label="ふりがな" labelProps={{ py: "18px" }}>
								<Stack direction="row" alignItems="center">
									<Switch
										color="primary"
										defaultChecked={options.showKana}
										onChange={(_, checked) => setOptions({ ...options, showKana: checked })}
									/>
									<Typography variant="body2" whiteSpace="nowrap">{options.showKana ? "表示" : "非表示"}</Typography>
								</Stack>
							</Section>
						</Stack>
					</>
				)}
			</Stack>
		</Paper>
	)
}